import { CSSProperties, useState } from "react";
import styled from "styled-components";
import media from "styled-media-query";
import { useNavigate } from "react-router";

import { Text, ColorV2, Button } from "@adoptaunabuelo/react-components";
import {
	Cake,
	Check,
	Flower,
	Heart,
	HeartCrack,
	Mail,
	Rocket,
	Smartphone,
	Star,
	Users,
} from "lucide-react";
import ActivitiesList from "../../../components/list/ActivitiesList";
import TigerIcons from "../../../assets/images/icon/Tiger";
import TestimonyList from "../../../components/list/TestimonyList";
import ReliefModal from "../../../components/modal/ReliefModal";
import { useSelector } from "react-redux";
import { State } from "../../../redux";
import ProtectedDonation from "../../../components/modal/ProtectedDonation";
import ContainerSection from "../../../components/landing/ContainerSection";

const screenSize1: any = "1350px";
const screenSize2: any = "1024px";

const Container = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const Section = styled.div`
	position: relative;
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const SectionFinal = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	padding: 0px 10%;
	justify-content: center;
	align-items: center;
	height: calc(var(--doc-height) - var(--nav-bar-height));
	${media.lessThan(screenSize1)`
        padding: 0px 5%;
    `}
	${media.lessThan(screenSize2)`
        padding: 0px 48px;
    `}
    ${media.lessThan("medium")`
        padding: 64px 0px;
    `}
    ${media.lessThan("small")`
        padding: 48px 0px;
    `}
`;
const ProgressContainer = styled.div`
	display: flex;
	gap: 16px;
	${media.lessThan("medium")`
        flex-direction: column;
	`}
`;
const ProgressView = styled.div`
	display: flex;
	flex-direction: column;
	background-color: ${ColorV2.surface.background};
	padding: 24px;
	border-radius: 24px;
	${media.lessThan("medium")`
        
	`}
`;
const Col = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const ColReverse = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	${media.lessThan("medium")`
        flex-direction: row;
	`}
`;
const Row = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
`;
const RowReverse = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	${media.lessThan("medium")`
        flex-direction: column;
	`}
`;
const Cell = styled.div`
	display: flex;
	flex-direction: row;
	margin-bottom: 12px;
`;
const IconView = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 64px;
	width: 64px;
	min-height: 64px;
	min-width: 64px;
	border-radius: 64px;
	background-color: ${ColorV2.surface.background};
	${media.lessThan("medium")`
    	height: 44px;
		width: 44px;
		min-height: 44px;
		min-width: 44px;
	`}
`;
const IconView2 = styled.div`
	height: 64px;
	width: 64px;
	min-height: 64px;
	min-width: 64px;
	margin-bottom: 16px;
`;
const CheckView = styled.div`
	display: flex;
	height: 22px;
	width: 22px;
	align-items: center;
	justify-content: center;
	margin-right: 12px;
`;
const PieChartCol = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 16px;
	${media.lessThan("medium")`
        width: -webkit-fill-available;
        width: -moz-available;
	`}
`;

const Info = (props: Props) => {
	const navigate = useNavigate();
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const [showRelief, setShowRelief] = useState(false);

	const chartOptions = [
		{
			title: "Actividades con personas mayores",
			value: 90,
			color: ColorV2.text.primary,
			textColor: ColorV2.text.primary,
			fontSize: 120,
			bullets: [
				"Los mayores no pagan nada por participar.",
				"Acompañamiento semanal.",
				"Celebramos su cumpleaños.",
				"Asistencia a nuestras actividades.",
				"Cumplimos sus sueños.",
				"Asistencia psicológica en casos de depresión.",
			],
		},
		{
			title: "Costes operacionales",
			value: 8,
			color: ColorV2.text.neutralHard,
			textColor: ColorV2.text.neutralHard,
			fontSize: 48,
			bullets: [
				"Nos permiten funcionar como organización (alquiler de oficina y suministros).",
				"Impuestos.",
				"Inversión en tecnología para llegar a todos los abuelos del mundo.",
			],
		},
		{
			title: "Comisiones bancarias",
			value: 2,
			color: ColorV2.text.neutralMedium,
			textColor: ColorV2.text.neutralMedium,
			fontSize: 32,
		},
	];

	const activities = [
		{
			name: "Ramón",
			info: "Cumplimos el sueño de mayores como Ramón. Su sueño era volver a jugar al béisbol. Gracias a vuestra ayuda, Ramón pudo disfrutar de un día inolvidable.",
			testimony:
				"No puedo creer todo lo que habéis hecho. Estoy como un niño pequeño.",
			img: require("../../../assets/images/history/ramon.webp"),
			video: "https://youtube.com/shorts/n72-NPDbAeY",
			label: {
				title: "Sueños",
				icon: (
					<Rocket
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
		{
			name: "Felisa",
			info: "Todas las navidades llenamos las residencias de felicitaciones y conseguimos que miles de mayores se sientan acompañados en estas fechas tan especiales.",
			testimony:
				"Me parece muy bonito porque, sin conocernos, da la sensación de que están a nuestro lado y que nos conocen.",
			img: require("../../../assets/images/history/felisa.webp"),
			video: "https://www.youtube.com/watch?v=D--PsMjx6Ys",
			label: {
				title: "Cartas",
				icon: (
					<Mail
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
		{
			name: "Loli",
			info: "Con vuestra ayuda, organizamos fiestas de cumpleaños para personas mayores, haciendo que su día sea inolvidable. ¡Tarta, velas, globos y muchos invitados!",
			testimony: "Les daría un abrazo a todos, porque sois maravillosos.",
			img: require("../../../assets/images/history/loli.webp"),
			video: "https://www.youtube.com/watch?v=9BoL3KmF97M&list=PL2bO3y1ZkJ312Ovt0hS_v3G6oZsPwQkkA",
			label: {
				title: "Cumpleaños",
				icon: (
					<Cake
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
		{
			name: "Alfonso",
			info: "Una actividad para reducir la brecha digital de las personas mayores, ofreciéndoles herramientas básicas para desenvolverse en el mundo tecnológico y ampliar sus opciones de socialización y ocio.",
			testimony:
				"Gracias a Adopta Un Abuelo ahora puedo hablar con mi nieto por el móvil todas las semanas.",
			img: require("../../../assets/images/history/alfonso.webp"),
			video: "https://www.youtube.com/watch?v=oi1HMWoW0VA",
			label: {
				title: "Actividades",
				icon: (
					<Flower
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
		{
			name: "Raquel",
			info: "A través de nuestro programa de voluntariado, acompañamos a personas mayores todas las semanas. Siempre resulta una experiencia enriquecedora tanto para mayores como para jóvenes.",
			testimony:
				"Gracias al programa de Adopta Un Abuelo ya tengo una amistad más.",
			img: require("../../../assets/images/history/raquel.webp"),
			video: "https://youtube.com/shorts/4jzq8rOR8l0",
			label: {
				title: "Voluntariado",
				icon: (
					<Heart
						height={20}
						width={20}
						color={ColorV2.text.neutralHard}
					/>
				),
			},
		},
	];

	const testimonies = [
		{
			name: "María Flores",
			image: require("../../../assets/images/testimonies/maria.webp"),
			testimony:
				"Conocer a África, mi abuela adoptada, ha sido increíble. He podido aprender cada día de ella y me ha dado mucho cariño y amor. África siempre me dice que cómo podemos ser tan buenas amigas con todos los años que nos llevamos, y es que para la amistad no hay edad. Es una experiencia única que te llena a nivel personal.",
		},
		{
			name: "Luis Silvestre",
			image: require("../../../assets/images/testimonies/luis.webp"),
			testimony:
				"Lo recomiendo 100% por compartir experiencias con las personas más sabias del planeta y por lo gratificante que es dedicarles tiempo a nuestros mayores que se encuentren solos.",
		},
		{
			name: "Gaby Lopez",
			image: require("../../../assets/images/testimonies/gaby.webp"),
			testimony:
				"Recomendaría Adopta Un Abuelo sin ninguna duda. Es una bonita manera de devolver lo que España me ha brindado durante estos 12 años, qué mejor forma que hacerlo dando cariño y atención a nuestros mayores. Me encantan los momentos divertidos con mi abuela Pilar.",
		},
		{
			name: "Mayra Camarillo",
			image: require("../../../assets/images/testimonies/mayra.webp"),
			testimony:
				"He estado hoy con mi hija viviendo la experiencia y la verdad que ha sido muy entrañable, ver tantos jóvenes comprometidos con la causa de dar cariño desinteresadamente y alegrar la tarde compartiendo su tiempo para celebrar la vida de estas dulces abuelas… Una gran labor que sin duda alguna merece la pena apoyar.",
		},
		{
			name: "Isa",
			image: require("../../../assets/images/testimonies/isa.webp"),
			testimony:
				"Ha sido un día muy especial, compartiendo un ratito junto a los abuelitos que tanta compañía necesitan, nos lo hemos pasado en grande y como siempre, hemos aprendido mucho a su lado. Gracias por la oportunidad de ser voluntaria y acompañarlos 🥰",
		},
		{
			name: "Dina Coy Aldana",
			image: require("../../../assets/images/testimonies/dina.webp"),
			testimony:
				"Increíble la labor que hacen. Hemos podido pasar la tarde con Cristina y José Luis, y nos han dicho que para ellos ese rato ha sido un regalo. Ha sido muy emotivo poder aprender de su experiencia",
		},
		{
			name: "Felix López",
			image: require("../../../assets/images/testimonies/felix.webp"),
			testimony:
				"Mejor labor imposible. Les sigo hace más de 6 años así que no podía dejar de ser socio y voluntario. He escrito cartas y he adoptado dos abuelos. Únete para luchar contra la soledad no deseada de miles de mayores. Tienen infinidad de iniciativas asi que únete. Hoy me vine al primer tripe cumpleaños, inmejorable ambiente. Toda la plantilla y el fundador volcados en dar una sorpresa y una tarde únicas a los mayores",
		},
		{
			name: "Luis Tapiador",
			image: require("../../../assets/images/testimonies/luis-2.webp"),
			testimony:
				"Simplemente increíble, yo creo que es de los mejores voluntariados que he participado, se nota el cariño que hacen esta labor. Felicidades a todo el equipo que hace posible esto. Un saludo y nos vemos en la próxima.",
		},
		{
			name: "Lourdes García",
			image: require("../../../assets/images/testimonies/lourdes.webp"),
			testimony:
				"Me parece una iniciativa increíble que hace que las personas mayores no se sientan tan solos. Necesitamos más organismos como este que hagan sus vidas un poco más felices. Una mención especial a Rosi por su siempre implicación y por su trabajo. Me parece única.",
		},
	];

	const renderProgressView = (item: any, hideMargin?: boolean) => (
		<ProgressView>
			<Text
				type="p"
				style={{
					fontSize: item.fontSize,
					lineHeight: item.fontSize + "px",
					fontWeight: 800,
					color: item.textColor,
				}}
			>
				{item.value}%
			</Text>
			<Text
				type="h4"
				weight="medium"
				style={{
					marginBottom: hideMargin ? 0 : 24,
					color: item.textColor,
				}}
			>
				{item.title}
			</Text>
			{item.bullets &&
				item.bullets.map((bulletItem: string, index: number) => (
					<Cell key={"progress-bullet-" + index}>
						<CheckView>
							<Check
								height={16}
								width={16}
								color={item.textColor}
							/>
						</CheckView>
						<Text type="p" style={{ flex: 1 }}>
							{bulletItem}
						</Text>
					</Cell>
				))}
		</ProgressView>
	);

	return (
		<Container style={props.style}>
			<ReliefModal
				isVisible={showRelief}
				price={15}
				charge="monthly"
				onClose={() => setShowRelief(false)}
			/>
			<ContainerSection style={{ gap: isMobile ? 32 : 16 }}>
				<Section style={isMobile ? {} : { marginRight: 48 }}>
					<Text
						type="h2"
						weight="semibold"
						style={{ marginBottom: 16 }}
					>
						¿Qué significa ser embajador?
					</Text>
					<Text type="p">
						Ser embajador es pertenecer a la comunidad de gente a la
						que le apasionan las personas mayores y nos ayudan
						económicamente en nuestra misión de acompañarlos.
					</Text>
				</Section>
				<Section
					style={{
						alignItems: "center",
						flexDirection: "row",
						gap: 16,
						marginTop: 16,
					}}
				>
					<IconView2>
						<TigerIcons type="grandma-heart" />
					</IconView2>
					<Text type="p">
						Con una{" "}
						<span style={{ fontWeight: 600 }}>
							aportación mensual o anual
						</span>
						, haces posible que nuestro equipo acompañe a cientos de
						mayores cada día.
					</Text>
				</Section>
				<Section
					style={{
						alignItems: "center",
						flexDirection: "row",
						gap: 16,
					}}
				>
					<IconView2>
						<TigerIcons type="coins" />
					</IconView2>
					<Text type="p">
						Además,{" "}
						<span style={{ fontWeight: 600, backgroundColor: ColorV2.surface.secondary, padding: "0px 2px" }}>
							te devuelven el 80%
						</span>{" "}
						de tu donación en la declaración de la renta, por lo que
						no te cuesta casi nada.{" "}
						<a
							style={{
								cursor: "pointer",
								textDecoration: "underline",
							}}
							onClick={() => setShowRelief(true)}
						>
							Saber más
						</a>
					</Text>
				</Section>
			</ContainerSection>
			<ContainerSection style={{ gap: isMobile ? 0 : (1 / 12) * 100 + "%" }}>
				<Section style={{ alignItems: "center" }}>
					<Col style={{ maxWidth: 750 }}>
						<Text
							type="h2"
							weight="semibold"
							style={{ marginBottom: 16, textAlign: "center" }}
						>
							Existimos porque las personas mayores nos necesitan
						</Text>
						<Text type="h6" style={{ textAlign: "center" }}>
							Estos son los principales problemas sociales a los
							que se enfrentan en su día a día.
						</Text>
					</Col>
					<RowReverse
						style={{ gap: 32, paddingTop: isMobile ? 48 : 80 }}
					>
						{[
							{
								icon: HeartCrack,
								title: "Soledad no deseada",
								subtitle:
									"+3.000.000 de personas mayores viven solas.",
							},
							{
								icon: Smartphone,
								title: "Brecha digital",
								subtitle:
									"En España solo el 30% de las personas mayores tiene habilidades digitales.",
							},
							{
								icon: Users,
								title: "Edadismo",
								subtitle:
									"Prejuicios y estereotipos que excluyen a los mayores y afectan negativamente su calidad de vida.",
							},
						].map((item, index) => (
							<ColReverse
								key={"problem-" + index}
								style={{
									gap: 12,
									alignItems: isMobile
										? "flex-start"
										: "center",
								}}
							>
								<IconView>
									<item.icon
										color={ColorV2.text.neutralHard}
										height={isMobile ? 24 : 32}
										width={isMobile ? 24 : 32}
									/>
								</IconView>
								<div>
									<Text
										type="h5"
										weight="medium"
										style={{
											textAlign: isMobile
												? "left"
												: "center",
										}}
									>
										{item.title}
									</Text>
									<Text
										type="p"
										style={{
											display: "flex",
											flex: 1,
											color: ColorV2.text.neutralMedium,
											marginTop: 5,
											textAlign: isMobile
												? "left"
												: "center",
										}}
									>
										{item.subtitle}
									</Text>
								</div>
							</ColReverse>
						))}
					</RowReverse>
				</Section>
			</ContainerSection>
			<ContainerSection>
				<Section style={{ flex: 4, marginRight: isMobile ? 0 : 48 }}>
					<Text
						type="h2"
						weight="semibold"
						style={{ marginBottom: isMobile ? 8 : 24 }}
					>
						Con tu ayuda, acompañamos a personas mayores todos los
						días
					</Text>
					<Text
						type="h6"
						style={{ marginBottom: isMobile ? 32 : 64 }}
					>
						Estas son algunas de las historias que hacemos posibles
						gracias a tu donación
					</Text>
				</Section>
				<Section style={{ flex: 8 }}>
					<ActivitiesList testimonies={activities} />
				</Section>
			</ContainerSection>
			<ContainerSection>
				<Section style={{ alignItems: "center" }}>
					<Text
						type="h2"
						weight="semibold"
						style={{ textAlign: "center" }}
					>
						A qué se destina tu donación
					</Text>
					<Text
						type="h6"
						style={{
							marginTop: 24,
							marginBottom: 24,
							textAlign: "center",
						}}
					>
						Somos una Fundación independiente y no recibimos
						subvenciones. Nuestra actividad se sostiene gracias al
						apoyo de personas comprometidas con las personas
						mayores.
					</Text>
					<ProtectedDonation
						style={{ marginBottom: 48 }}
						type="donation"
					/>
					<ProgressContainer>
						<PieChartCol>
							{renderProgressView(chartOptions[0])}
						</PieChartCol>
						<PieChartCol>
							{renderProgressView(chartOptions[1])}
							{renderProgressView(chartOptions[2], true)}
						</PieChartCol>
					</ProgressContainer>
				</Section>
			</ContainerSection>
			<ContainerSection>
				<Section>
					<Text
						type="h2"
						weight="semibold"
						style={{ marginBottom: 16 }}
					>
						Opiniones de quienes hacen esto posible
					</Text>
					<Col
						style={{
							marginBottom: isMobile ? 24 : 0,
						}}
					>
						<Row
							style={{
								alignItems: "center",
								gap: 8,
							}}
						>
							<Text type="h4" weight="semibold">
								4,9
							</Text>
							<div>
								<Star
									fill={ColorV2.surface.secondary}
									color={ColorV2.surface.secondary}
								/>
								<Star
									fill={ColorV2.surface.secondary}
									color={ColorV2.surface.secondary}
								/>
								<Star
									fill={ColorV2.surface.secondary}
									color={ColorV2.surface.secondary}
								/>
								<Star
									fill={ColorV2.surface.secondary}
									color={ColorV2.surface.secondary}
								/>
								<Star
									fill={ColorV2.surface.secondary}
									color={ColorV2.surface.secondary}
								/>
							</div>
						</Row>
						<Text
							type="p"
							style={{ color: ColorV2.text.neutralMedium }}
						>
							+1.850 reseñas
						</Text>
					</Col>
					<TestimonyList options={testimonies} nCells={3} />
				</Section>
			</ContainerSection>
			<SectionFinal>
				<Text
					type="h2"
					style={{
						textAlign: "center",
					}}
				>
					Hemos acompañado a miles de personas mayores en situación de
					soledad no deseada,
				</Text>
				<Text
					type="h2"
					weight="semibold"
					style={{
						textAlign: "center",
						color: ColorV2.text.primary,
					}}
				>
					pero todavía nos quedan millones por acompañar.
				</Text>
				{!isMobile && (
					<Button
						style={{
							width: "fit-content",
							backgroundColor: ColorV2.surface.primaryHard,
							display: "flex",
							marginTop: 48,
						}}
						onClick={() => navigate("/consejo/registro")}
					>
						Quiero ser embajador
					</Button>
				)}
			</SectionFinal>
		</Container>
	);
};
export default Info;
export interface Props {
	style?: CSSProperties;
	children?: React.ReactNode;
	onClick?: () => void;
}
